'use client';

import { useSearchParams } from 'next/navigation';

import { tss } from 'tss-react/mui';

import { Box } from '@mui/material';

import { use } from 'react';

import type { DocumentType } from '@/gql';
import { graphql } from '@/gql';
import { getFileDisplayName } from '@/utils/file/fileUtils';
import { Or2Link } from '@/components/navigation/Or2Link';
import { FileManagerContext } from '@/modules/files/contexts/FileManagerContext';

const FILE_LINK_FRAGMENT = graphql(`
  fragment fileLinkFields on File {
    ...fileMiniFields
    parent {
      _id
      filename
    }
  }
`);

interface Props {
  file: DocumentType<typeof FILE_LINK_FRAGMENT>;
  showParentLink?: boolean;
  isDragging: boolean;
}

const useStyles = tss.create(({ theme }) => ({
  link: {
    color: theme.palette.text.primary,
    wordBreak: 'break-word',
    textDecoration: 'none',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  parent: {
    fontSize: theme.typography.caption.fontSize,
  },
}));

export const FileLink = ({ file, showParentLink, isDragging }: Props) => {
  const { cx, classes } = useStyles();

  const { rootOwner } = use(FileManagerContext);

  const searchParams = useSearchParams();

  const { _id: fileId, url, folder, parent } = file;
  const fileDisplayName = getFileDisplayName(file);

  const { type, machineName } = rootOwner ?? {};

  const pathnameWithoutFileId = `/${type}/${machineName}/${type === 'g' ? 'docs' : 'resources'}`;

  const searchParamsString = searchParams.toString()
    ? `?${searchParams.toString()}`
    : '';

  const newUrl = folder
    ? `${pathnameWithoutFileId}/${fileId}${searchParamsString}`
    : `/document/${fileId}/view`;

  const parentUrl = `${pathnameWithoutFileId}/${
    parent?._id
  }${searchParamsString}`;

  if (url) {
    return (
      <div>
        {isDragging ? (
          <Box component="span" className={classes.link}>
            {fileDisplayName}
          </Box>
        ) : (
          <Or2Link href={url} isExternal className={classes.link}>
            {fileDisplayName}
          </Or2Link>
        )}
        {showParentLink && parent && (
          <div className={cx(classes.link, classes.parent)}>
            <Or2Link href={parentUrl} className={classes.link}>
              {`in ${parent?.filename}`}
            </Or2Link>
          </div>
        )}
      </div>
    );
  }
  return (
    <div>
      {isDragging ? (
        <Box component="span" className={classes.link}>
          {fileDisplayName}
        </Box>
      ) : (
        <Or2Link
          target={folder ? undefined : '_blank'}
          href={newUrl}
          className={classes.link}
        >
          {fileDisplayName}
        </Or2Link>
      )}
      {showParentLink && parent && (
        <div className={cx(classes.link, classes.parent)}>
          <Or2Link href={parentUrl} className={classes.link}>
            {`in ${parent?.filename}`}
          </Or2Link>
        </div>
      )}
    </div>
  );
};
